<template>
  <div class="app-shell">
    <Teleport to="body">
      <Transition name="login-success-toast">
        <div
          v-if="loginSuccessToastVisible"
          class="login-success-toast"
          role="status"
          aria-live="polite"
        >
          <div class="login-success-toast__inner">
            <span class="login-success-toast__icon" aria-hidden="true">✓</span>
            <div class="login-success-toast__body">
              <strong class="login-success-toast__title">登录成功</strong>
              <span class="login-success-toast__hint">欢迎，{{ loginSuccessWelcomeName }}</span>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
    <Teleport to="body">
      <Transition name="exam-app-dialog">
        <div
          v-if="appDialogVisible"
          class="exam-app-dialog-wrap"
          role="presentation"
        >
          <div class="exam-app-dialog-backdrop" @click="onAppDialogBackdrop"></div>
          <div
            class="exam-app-dialog-panel-outer"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="exam-app-dialog-title"
          >
            <div class="login-success-toast__inner exam-app-dialog-panel">
              <div class="exam-app-dialog-head">
                <span class="login-success-toast__icon exam-app-dialog-icon" aria-hidden="true">i</span>
                <strong id="exam-app-dialog-title" class="exam-app-dialog-title-text">请注意</strong>
              </div>
              <div class="exam-app-dialog-message">{{ appDialogMessage }}</div>
              <div class="exam-app-dialog-actions">
                <template v-if="appDialogType === 'alert'">
                  <button
                    type="button"
                    class="exam-app-dialog-btn exam-app-dialog-btn--primary"
                    @click="closeAppDialog"
                  >
                    确定
                  </button>
                </template>
                <template v-else>
                  <button type="button" class="exam-app-dialog-btn" @click="appDialogConfirm(false)">
                    取消
                  </button>
                  <button
                    type="button"
                    class="exam-app-dialog-btn exam-app-dialog-btn--primary"
                    @click="appDialogConfirm(true)"
                  >
                    确定
                  </button>
                </template>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
    <router-view />
  </div>
</template>

<script>
import {
  APP_ALERT_EVENT,
  APP_CONFIRM_EVENT,
  resolveAppConfirm
} from './utils/appDialog'

export default {
  name: 'App',
  data() {
    return {
      activityPingTimer: null,
      loginSuccessToastVisible: false,
      loginSuccessToastTimer: null,
      loginSuccessWelcomeName: '用户',
      appDialogVisible: false,
      appDialogType: 'alert',
      appDialogMessage: ''
    }
  },
  mounted() {
    window.addEventListener('exam-login-success-toast', this.showLoginSuccessToast)
    window.addEventListener(APP_ALERT_EVENT, this.onAppAlertEvent)
    window.addEventListener(APP_CONFIRM_EVENT, this.onAppConfirmEvent)
    this.sendActivityPing()
    this.activityPingTimer = setInterval(() => this.sendActivityPing(), 45000)
  },
  beforeUnmount() {
    window.removeEventListener('exam-login-success-toast', this.showLoginSuccessToast)
    window.removeEventListener(APP_ALERT_EVENT, this.onAppAlertEvent)
    window.removeEventListener(APP_CONFIRM_EVENT, this.onAppConfirmEvent)
    if (this.activityPingTimer) clearInterval(this.activityPingTimer)
    if (this.loginSuccessToastTimer) clearTimeout(this.loginSuccessToastTimer)
  },
  methods: {
    showLoginSuccessToast() {
      let name = '用户'
      try {
        const raw = localStorage.getItem('exam_user')
        if (raw) {
          const u = JSON.parse(raw)
          const acc = u && (u.username != null ? u.username : u.userName)
          if (acc != null && String(acc).trim() !== '') {
            name = String(acc).trim()
          }
        }
      } catch (e) {
        /* ignore */
      }
      this.loginSuccessWelcomeName = name
      if (this.loginSuccessToastTimer) {
        clearTimeout(this.loginSuccessToastTimer)
        this.loginSuccessToastTimer = null
      }
      this.loginSuccessToastVisible = true
      this.loginSuccessToastTimer = setTimeout(() => {
        this.loginSuccessToastVisible = false
        this.loginSuccessToastTimer = null
      }, 5000)
    },
    onAppAlertEvent(e) {
      this.appDialogType = 'alert'
      this.appDialogMessage = (e && e.detail && e.detail.message) || ''
      this.appDialogVisible = true
    },
    onAppConfirmEvent(e) {
      this.appDialogType = 'confirm'
      this.appDialogMessage = (e && e.detail && e.detail.message) || ''
      this.appDialogVisible = true
    },
    closeAppDialog() {
      this.appDialogVisible = false
    },
    appDialogConfirm(ok) {
      resolveAppConfirm(ok)
      this.appDialogVisible = false
    },
    onAppDialogBackdrop() {
      if (this.appDialogType === 'confirm') {
        this.appDialogConfirm(false)
      } else {
        this.closeAppDialog()
      }
    },
    sendActivityPing() {
      try {
        const raw = localStorage.getItem('exam_user')
        if (!raw) return
        const u = JSON.parse(raw)
        if (!u || u.id == null) return
        fetch('/api/activity/ping', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: u.id })
        }).catch(() => {})
      } catch (e) {
        /* ignore */
      }
    }
  }
}
</script>

<style>
* {
  box-sizing: border-box;
}

:root {
  --bg-0: #f7faff;
  --bg-1: #eef4ff;
  --bg-2: #e7f0ff;
  --panel: #ffffff;
  --panel-soft: #f8fbff;
  --glass: rgba(255, 255, 255, 0.78);
  --line: #d8e6ff;
  --line-strong: #bfd6ff;
  --text-main: #1f2a44;
  --text-sub: #5c7098;
  --primary: #2f6feb;
  --primary-2: #5ca2ff;
  --shadow-soft: 0 10px 26px rgba(48, 96, 192, 0.1);
  --shadow-hover: 0 14px 30px rgba(56, 104, 196, 0.16);
}

body {
  margin: 0;
  min-height: 100vh;
  font-family: Inter, "PingFang SC", "Microsoft YaHei", Helvetica, Arial, sans-serif;
  color: var(--text-main);
  background:
    radial-gradient(circle at 10% 10%, rgba(92, 162, 255, 0.18), transparent 34%),
    radial-gradient(circle at 88% 18%, rgba(66, 131, 255, 0.14), transparent 30%),
    linear-gradient(160deg, var(--bg-0) 0%, var(--bg-1) 52%, #f9fbff 100%);
}

body.city-signup-modal-open {
  overflow: hidden;
}

#app {
  min-height: 100vh;
}

.app-shell {
  min-height: 100vh;
}

/* global page shells */
.login-page,
.register-page,
.role-page,
.teacher-page,
.dashboard-page {
  position: relative;
  background:
    radial-gradient(circle at 8% 10%, rgba(82, 149, 255, 0.16), transparent 34%),
    radial-gradient(circle at 92% 20%, rgba(120, 170, 255, 0.14), transparent 30%),
    linear-gradient(160deg, var(--bg-0) 0%, var(--bg-1) 55%, #f8fbff 100%) !important;
  color: var(--text-main) !important;
}

.login-page::before,
.register-page::before,
.role-page::before,
.teacher-page::before,
.dashboard-page::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: linear-gradient(rgba(104, 143, 218, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(104, 143, 218, 0.05) 1px, transparent 1px);
  background-size: 26px 26px;
}

.side-nav,
.sidebar {
  background: var(--glass) !important;
  border: 1px solid var(--line) !important;
  box-shadow: 0 12px 30px rgba(63, 108, 199, 0.1) !important;
  backdrop-filter: blur(8px);
}

.content-area,
.main-panel {
  position: relative;
  z-index: 1;
}

.topbar,
.content-card,
.card-box,
.card-box-sm,
.modal-panel,
.login-card,
.register-card {
  background: linear-gradient(165deg, var(--glass) 0%, rgba(248, 251, 255, 0.92) 100%) !important;
  border: 1px solid var(--line) !important;
  color: var(--text-main) !important;
  box-shadow: var(--shadow-soft) !important;
  backdrop-filter: blur(10px);
}

.nav-btn,
.sub-btn,
.group-header,
.sidebar li {
  background: #f6f9ff !important;
  color: #335896 !important;
  border: 1px solid var(--line) !important;
  transition: all 0.2s ease;
}

.nav-btn.active,
.sub-btn.active {
  background: linear-gradient(120deg, var(--primary), var(--primary-2)) !important;
  color: #fff !important;
  border-color: transparent !important;
}

.nav-btn:hover,
.sub-btn:hover,
.group-header:hover,
.sidebar li:hover {
  background: #eef5ff !important;
  border-color: var(--line-strong) !important;
  transform: translateY(-1px);
}

/* Student/Teacher 左侧菜单与管理员端统一风格 */
.teacher-page .side-nav .sub-btn,
.student-page .side-nav .nav-btn,
.role-page .side-nav .nav-btn {
  border: 1px solid #aeb9c8 !important;
  border-left: 6px solid #8398b0 !important;
  border-radius: 10px !important;
  padding: 12px 14px !important;
  color: #111827 !important;
  background: #dbe9ff !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  line-height: 1.35;
  box-shadow: 0 2px 5px rgba(15, 23, 42, 0.1) !important;
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease !important;
}

.teacher-page .side-nav .sub-btn:hover,
.student-page .side-nav .nav-btn:hover,
.role-page .side-nav .nav-btn:hover {
  color: #0b1322 !important;
  background: #cfe2ff !important;
  border-color: #93a4b9 !important;
  border-left-color: #667f9d !important;
  box-shadow: 0 5px 12px rgba(15, 23, 42, 0.18) !important;
  transform: translateY(-1px);
}

.teacher-page .side-nav .sub-btn.active,
.student-page .side-nav .nav-btn.active,
.role-page .side-nav .nav-btn.active {
  color: #09111f !important;
  background: #bed6ff !important;
  border-color: #6f87a7 !important;
  border-left: 8px solid #294e79 !important;
  box-shadow: 0 7px 16px rgba(31, 57, 92, 0.28), inset 0 0 0 1px rgba(255, 255, 255, 0.35) !important;
}

.teacher-page .side-nav .sub-btn.active:hover,
.student-page .side-nav .nav-btn.active:hover,
.role-page .side-nav .nav-btn.active:hover {
  color: #061020 !important;
  background: #b2ceff !important;
  border-color: #607da1 !important;
  border-left: 8px solid #1f446f !important;
}

.modern-table > :not(caption) > * > *,
.table > :not(caption) > * > * {
  color: var(--text-main) !important;
  border-bottom-color: #e4edff !important;
}

.modern-table thead th,
.table thead th {
  background: #f2f7ff !important;
  color: #2b4e92 !important;
  font-weight: 600;
}

.modern-table tbody tr:hover td,
.table tbody tr:hover td {
  background: #f7faff !important;
}

.result-shell {
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 10px;
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.82), rgba(245, 250, 255, 0.9));
}

.query-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
}

.query-card {
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 12px;
  background: linear-gradient(165deg, #ffffff, #f8fbff);
  box-shadow: 0 6px 14px rgba(49, 97, 193, 0.08);
}

.query-card .k {
  font-size: 12px;
  color: var(--text-sub);
  margin-bottom: 2px;
}

.query-card .v {
  color: var(--text-main);
  font-weight: 600;
  word-break: break-word;
  margin-bottom: 8px;
}

.form-label,
.form-label-soft,
.page-subtitle,
.hint-text,
.text-muted {
  color: var(--text-sub) !important;
}

h1,
h2,
h3,
h4,
h5,
h6,
.page-title,
.topbar-title {
  color: var(--text-main) !important;
  font-weight: 700 !important;
  letter-spacing: 0.2px;
}

h1,
h2,
h3,
h4,
h5,
h6,
.page-title,
.topbar-title {
  color: var(--text-main) !important;
  font-weight: 700 !important;
  letter-spacing: 0.2px;
}

.form-control,
.form-select {
  background: #fbfdff !important;
  color: var(--text-main) !important;
  border: 1px solid #cfe0ff !important;
  border-radius: 10px !important;
}

.form-control::placeholder {
  color: #92a9cf !important;
}

.form-control:focus,
.form-select:focus {
  border-color: #79a7ff !important;
  box-shadow: 0 0 0 0.2rem rgba(82, 136, 240, 0.16) !important;
}

.btn {
  border-radius: 10px !important;
  font-weight: 600 !important;
  transition: all 0.22s ease !important;
}

.btn-success,
.btn-primary,
.teacher-btn,
.logout-btn,
.login-btn,
.register-btn,
.captcha-btn {
  background: linear-gradient(120deg, var(--primary), var(--primary-2)) !important;
  border-color: var(--primary) !important;
  color: #fff !important;
}

.btn-warning,
.btn-secondary,
.btn-light,
.btn-danger,
.btn-info,
.btn-outline-light {
  background: #f4f8ff !important;
  border-color: #cfe0ff !important;
  color: #355b9f !important;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 16px rgba(60, 108, 205, 0.16);
}

.card-box:hover,
.content-card:hover,
.login-card:hover,
.register-card:hover {
  box-shadow: var(--shadow-hover) !important;
  transform: translateY(-1px);
}

.nav-btn,
.sub-btn,
.group-header,
.card-box,
.content-card,
.login-card,
.register-card,
.topbar,
.modal-panel,
.btn {
  will-change: transform, box-shadow, background-color, border-color;
}

.card-box:hover,
.content-card:hover,
.login-card:hover,
.register-card:hover {
  box-shadow: var(--shadow-hover) !important;
  transform: translateY(-1px);
}

.nav-btn,
.sub-btn,
.group-header,
.card-box,
.content-card,
.login-card,
.register-card,
.topbar,
.modal-panel,
.btn {
  will-change: transform, box-shadow, background-color, border-color;
}

.card-box:hover,
.content-card:hover,
.login-card:hover,
.register-card:hover {
  box-shadow: var(--shadow-hover) !important;
  transform: translateY(-1px);
}

.nav-btn,
.sub-btn,
.group-header,
.card-box,
.content-card,
.login-card,
.register-card,
.topbar,
.modal-panel,
.btn {
  will-change: transform, box-shadow, background-color, border-color;
}

.card-box:hover,
.content-card:hover,
.login-card:hover,
.register-card:hover {
  box-shadow: var(--shadow-hover) !important;
  transform: translateY(-1px);
}

.nav-btn,
.sub-btn,
.group-header,
.card-box,
.content-card,
.login-card,
.register-card,
.topbar,
.modal-panel,
.btn {
  will-change: transform, box-shadow, background-color, border-color;
}

/* responsive layout */
@media (max-width: 992px) {
  .role-page.d-flex,
  .teacher-page.d-flex,
  .dashboard-page {
    flex-direction: column !important;
  }

  .side-nav,
  .sidebar {
    width: 100% !important;
    min-height: auto !important;
    margin-right: 0 !important;
    margin-bottom: 12px !important;
  }
}

@media (max-width: 768px) {
  .role-page,
  .teacher-page,
  .dashboard-page,
  .login-page,
  .register-page {
    padding: 12px !important;
  }

  .card-box,
  .content-card,
  .topbar,
  .login-card,
  .register-card,
  .modal-panel {
    padding: 12px !important;
    border-radius: 12px !important;
  }

  .table {
    font-size: 12px;
  }

  .result-shell {
    padding: 8px;
  }
}

/* 登录成功顶部提示：白底 + 浅天蓝，与登录页校园风一致 */
.login-success-toast {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10050;
  display: flex;
  justify-content: center;
  padding: 18px 16px 0;
  pointer-events: none;
}

.login-success-toast__inner {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  max-width: min(440px, calc(100% - 8px));
  padding: 16px 20px;
  background: #ffffff;
  border: 1px solid #bae6fd;
  border-radius: 14px;
  border-left: 4px solid #0ea5e9;
  box-shadow:
    0 4px 6px -1px rgba(15, 23, 42, 0.06),
    0 12px 32px -8px rgba(14, 165, 233, 0.2),
    0 20px 50px -12px rgba(15, 23, 42, 0.08);
}

.login-success-toast__icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(145deg, #e0f2fe 0%, #bae6fd 100%);
  color: #0284c7;
  font-size: 18px;
  font-weight: 800;
  line-height: 1;
}

.login-success-toast__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.login-success-toast__title {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.06em;
}

.login-success-toast__hint {
  font-size: 0.8125rem;
  color: #64748b;
  line-height: 1.5;
}

.login-success-toast-enter-active,
.login-success-toast-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.login-success-toast-enter-from,
.login-success-toast-leave-to {
  opacity: 0;
  transform: translateY(-18px);
}

/* 全局「请注意」弹窗：与登录成功条同款白底 + 浅天蓝 */
.exam-app-dialog-wrap {
  position: fixed;
  inset: 0;
  z-index: 10060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.exam-app-dialog-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.38);
}

.exam-app-dialog-panel-outer {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: min(460px, 100%);
  pointer-events: auto;
}

.exam-app-dialog-panel.login-success-toast__inner {
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  max-width: none;
  text-align: left;
}

.exam-app-dialog-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.exam-app-dialog-icon.login-success-toast__icon {
  font-style: italic;
  font-weight: 900;
  font-size: 16px;
  font-family: Georgia, 'Times New Roman', serif;
}

.exam-app-dialog-title-text {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.12em;
}

.exam-app-dialog-message {
  font-size: 0.9375rem;
  color: #475569;
  line-height: 1.65;
  margin-bottom: 20px;
  white-space: pre-wrap;
  word-break: break-word;
}

.exam-app-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.exam-app-dialog-btn {
  min-width: 88px;
  padding: 9px 18px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  cursor: pointer;
  border: 1px solid #bae6fd;
  background: #ffffff;
  color: #0284c7;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.exam-app-dialog-btn:hover {
  background: #f0f9ff;
  border-color: #7dd3fc;
}

.exam-app-dialog-btn--primary {
  border: none;
  background: linear-gradient(180deg, #0ea5e9 0%, #0284c7 100%);
  color: #ffffff !important;
  box-shadow: 0 4px 12px rgba(2, 132, 199, 0.25);
}

.exam-app-dialog-btn--primary:hover {
  filter: brightness(1.03);
}

.exam-app-dialog-enter-active,
.exam-app-dialog-leave-active {
  transition: opacity 0.3s ease;
}

.exam-app-dialog-enter-active .exam-app-dialog-panel-outer,
.exam-app-dialog-leave-active .exam-app-dialog-panel-outer {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.exam-app-dialog-enter-from,
.exam-app-dialog-leave-to {
  opacity: 0;
}

.exam-app-dialog-enter-from .exam-app-dialog-panel-outer,
.exam-app-dialog-leave-to .exam-app-dialog-panel-outer {
  opacity: 0;
  transform: translateY(-12px) scale(0.98);
}
</style>
